import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import SparkMD5 from 'spark-md5';

import { URL_REPLACEMENT } from '$const';
import { RelationFile } from '~modules/media-library/controller/thunks/assetRelationsSlice';
import { AssetId } from '~modules/media-library/model/Asset';
import { get, patch, post, remove } from '~services/api/methods';

// @TODO: rename file to contribute.ts

// Upload - Get upload ticket
export const getUploadTicket = async () => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/request_upload_ticket`;

  return await get(requestUrl, { useBearerToken: true });
};

// upload without tus
export const sendFile = async (file: File, uploadUrl: string, progress: (progress: number) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('filename', file.name);

  const onUploadProgress = progressEvent => {
    const percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
    progress(percentCompleted);
  };

  const options = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  };

  return await post(uploadUrl, formData, options);
};

// Upload with Tus protocol using Uppy
export const sendFileWithTus = (
  uppyAuthToken: string,
  file: File,
  uploadUrl: string,
  progress: (progress: number) => void,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    // Calculate hash if not provided
    const calculateHash = async () => {
      return new Promise<string>((resolveHash, rejectHash) => {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const binaryStr = e.target?.result as string;
            // You can use the full file content for the hash
            const checksum = SparkMD5.hashBinary(binaryStr);
            resolveHash(checksum);
          } catch (err) {
            rejectHash(err);
          }
        };
        reader.onerror = rejectHash;
        reader.readAsBinaryString(file);
      });
    };

    const base64encode = str => {
      return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
          return String.fromCharCode(parseInt('0x' + p1));
        }),
      );
    };

    // Create a new Uppy instance
    const uppy = new Uppy({
      id: 'tusUploader',
      autoProceed: true,
      debug: process.env.NODE_ENV !== 'production',
    });

    // Calculate hash and then proceed with upload
    calculateHash()
      .then(calculatedHash => {
        // Add the Tus plugin with correct options
        uppy.use(Tus, {
          endpoint: uploadUrl,
          chunkSize: 5000000,
          retryDelays: [0, 1000, 3000, 5000],
          allowedMetaFields: null,
          headers: {
            'uppy-auth-token': uppyAuthToken,
          },
          async onBeforeRequest(req) {
            const method = req.getMethod ? req.getMethod() : '';

            if (method === 'POST') {
              // Encode the file names
              // Extract and re add the file extension to allow for detection of file types
              const parts = file.name.split('.');
              const extension = parts.pop();
              const safefilename = base64encode(`${parts.join('.')}`) + `.${extension}`;
              const uploadName = safefilename.replace(/\//g, 'RS_FORWARD_SLASH'); // To fix issue with forward slashes in base64 string
              const shortenedName = base64encode(uploadName.substring(uploadName.length - 30));

              // Set the metadata header with properly encoded filename
              req.setHeader('Upload-Metadata', `filename ${shortenedName}`);
            }

            if (calculatedHash) {
              const base64Hash = btoa(unescape(encodeURIComponent(calculatedHash)));

              const checksumHeader = `md5 ${base64Hash}`;
              req.setHeader('Upload-Checksum', checksumHeader);
            }
          },
        });

        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
        });

        // Track upload progress
        uppy.on('upload-progress', (file, progressData) => {
          const percentCompleted = Math.floor((progressData.bytesUploaded * 100) / progressData.bytesTotal);
          progress(percentCompleted);
        });

        // Handle successful upload
        uppy.on('complete', result => {
          if (result.successful && result.successful.length > 0) {
            resolve(result.successful[0].response);
          } else {
            reject(new Error('Upload failed'));
          }
          uppy.cancelAll();
        });

        // Add error handling
        uppy.on('error', error => {
          console.error('Uppy error:', error);
          reject(error);
        });
      })
      .catch(error => {
        console.error('Error calculating hash:', error);
        reject(error);
      });
  });
};

// Upload - Commit
export const commitUpload = async (uploadTicket: string, fileName: string) => {
  const formData = new FormData();
  formData.append('uploadTicket', uploadTicket);
  formData.append('fileName', fileName);

  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/commit_upload`;

  return await post(requestUrl, formData, { useBearerToken: true });
};

// Upload - commitUploadOfRelatedFile
export const commitUploadOfRelatedFile = async (
  uploadTicket: string,
  fileName: string,
  uploadType: string,
  mainAssetId: string,
) => {
  const formData = new FormData();
  formData.append('uploadTicket', uploadTicket);
  formData.append('fileName', fileName);
  formData.append('uploadType', uploadType);
  formData.append('mainAssetId', mainAssetId);

  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/commit_upload`;

  return await post(requestUrl, formData, { useBearerToken: true });
};

// Upload - Binary Update
export const commitBinaryUpdate = async (uploadTicket: string, fileName: string, assetId: AssetId) => {
  const data = {
    uploadTicket: uploadTicket,
    fileName: fileName,
    assetId: assetId,
  };

  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/commit_binary_update`;

  return await patch(requestUrl, data, { useBearerToken: true, useXwwwFormUrlencoded: true });
};

// remove upload asset (?)
export const removeAsset = async (assetId: string, uploadTicket: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/remove_asset`;
  const formData = new FormData();
  formData.append('assetId', assetId);
  formData.append('uploadTicket', uploadTicket);

  return await post(requestUrl, formData, { useBearerToken: true });
};

// remove relation of asset
export const removeRelation = async (file: RelationFile) => {
  const { assetId, relationId, relationType, relatedAssetId } = file;
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/relations/${relationId}/${relationType}/${assetId}/${relatedAssetId}`;

  return await remove(requestUrl, { useBearerToken: true });
};

// get contribution asset draft
export const getContributionAssetDraft = async (assetId: string, contributionType: string) => {
  const baseUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/draft/${assetId}`;
  const requestUrl = contributionType ? `${baseUrl}/${contributionType}` : baseUrl;

  return await get(requestUrl, { useBearerToken: true });
};

export const getAssetDuplicates = async (hash: string, assetTypes: string[] = null): Promise<string[]> => {
  let baseUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/asset_get_duplicates/${hash}`;
  // add asset types filter to url if is defined
  if (assetTypes) {
    const params = new URLSearchParams();
    params.set('assetTypes', assetTypes.toString());
    baseUrl = `${baseUrl}?${params}`;
  }
  return await get(baseUrl, { useBearerToken: true });
};

// check if asset has duplicates
export const checkAssetDuplicates = async (hash: string) => {
  const baseUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/asset_has_duplicates/${hash}`;

  return await get(baseUrl, { useBearerToken: true });
};

// get relation list
export const getRelationList = async (assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/relations/${assetId}`;

  return await get(requestUrl, { useBearerToken: true });
};

// Add relation
export const addRelation = async (documentType: string, mainAssetId: string, relatedAssetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/relations/${documentType}/${mainAssetId}/${relatedAssetId}`;

  return await post(requestUrl, { useBearerToken: true });
};

// get contribution asset
export const getPublishedContributionAsset = async (assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/asset/${assetId}`;

  return await get(requestUrl, { useBearerToken: true });
};

// Save Contribution Draft
export const saveContributionAsset = async (data: any, assetId: string, contributionType?: string) => {
  const baseUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/draft/${assetId}`;
  const requestUrl = contributionType ? `${baseUrl}/${contributionType}` : baseUrl;

  return await post(requestUrl, data, { useBearerToken: true });
};

// Save asset data from form
export const postAssetData = async (data: any, assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/asset/${assetId}`;

  return await post(requestUrl, data, { useBearerToken: true });
};

// Post apporval form data
export const postApprovalForm = async (data: any, assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/review/${assetId}`;

  return await post(requestUrl, data, { useBearerToken: true });
};

// Post apporval restart contribution
export const postRestartContribution = async (assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/contribute/api/rejected_to_draft/${assetId}`;

  return await post(requestUrl, {}, { useBearerToken: true });
};

// remove asset
export const deleteAsset = async (assetId: string) => {
  const requestUrl = `${URL_REPLACEMENT.SYMFONY_URL}/mamlibrary/delete/${assetId}`;

  return await remove(requestUrl, { useBearerToken: true });
};
